import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Printer, TrendingUp, Download, Mail } from 'lucide-react';
import { getActivityHistory, getGoalsHistory } from '../utils/api';
import { formatDateDisplay } from '../utils/weekUtils';
import { toast } from 'sonner';
import WeekCalendar from '../components/WeekCalendar';
import AgentLayout from '../components/AgentLayout';
import SendReportModal from '../components/SendReportModal';

const ReportsHistory = () => {
  const navigate = useNavigate();
  const [agentInfo, setAgentInfo] = useState(null);
  const [activities, setActivities] = useState([]);
  const [goals, setGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewType, setViewType] = useState('activity');
  const [sendModal, setSendModal] = useState({ open: false, type: '', week: '' });
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  // Generate all weeks that touch this month (Sun-Sat)
  const weeksInMonth = useMemo(() => {
    const year = currentMonth.year;
    const month = currentMonth.month;
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);

    // Find the Sunday on or before the 1st of the month
    const startSunday = new Date(firstOfMonth);
    startSunday.setDate(firstOfMonth.getDate() - firstOfMonth.getDay());

    // Find the Saturday on or after the last day of the month
    const endSaturday = new Date(lastOfMonth);
    endSaturday.setDate(lastOfMonth.getDate() + (6 - lastOfMonth.getDay()));

    const weeks = [];
    let current = new Date(startSunday);
    while (current <= endSaturday) {
      const sunday = new Date(current);
      const saturday = new Date(current);
      saturday.setDate(sunday.getDate() + 6);
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      weeks.push({ start: fmt(sunday), end: fmt(saturday) });
      current.setDate(current.getDate() + 7);
    }
    return weeks;
  }, [currentMonth]);

  // Selected week for calendar highlight (first week by default)
  const [selectedWeek, setSelectedWeek] = useState(null);

  useEffect(() => {
    if (weeksInMonth.length > 0 && !selectedWeek) {
      // Find the week containing today, or default to first week
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const todayWeek = weeksInMonth.find(w => todayStr >= w.start && todayStr <= w.end);
      setSelectedWeek(todayWeek || weeksInMonth[0]);
    }
  }, [weeksInMonth, selectedWeek]);

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    const info = localStorage.getItem('agentInfo');
    if (!token || !info) { navigate('/agent/login'); return; }
    setAgentInfo(JSON.parse(info));
    loadHistory(token);
  }, [navigate]);

  const loadHistory = async (token) => {
    try {
      setIsLoading(true);
      const [activityResult, goalsResult] = await Promise.all([
        getActivityHistory(token),
        getGoalsHistory(token)
      ]);
      if (activityResult.success) setActivities(activityResult.activities);
      if (goalsResult.success) setGoals(goalsResult.goals);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load reports history');
    } finally {
      setIsLoading(false);
    }
  };

  const monthName = new Date(currentMonth.year, currentMonth.month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const goToPrevMonth = () => {
    setCurrentMonth(prev => {
      const m = prev.month - 1;
      return m < 0 ? { year: prev.year - 1, month: 11 } : { year: prev.year, month: m };
    });
    setSelectedWeek(null);
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => {
      const m = prev.month + 1;
      return m > 11 ? { year: prev.year + 1, month: 0 } : { year: prev.year, month: m };
    });
    setSelectedWeek(null);
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setCurrentMonth({ year: now.getFullYear(), month: now.getMonth() });
    setSelectedWeek(null);
  };

  const handleCalendarWeekSelect = (start, end) => {
    setSelectedWeek({ start, end });
  };

  // Match activity/goal records to weeks in this month
  const getReportForWeek = (weekStart) => {
    return activities.find(a => a.weekStartDate === weekStart);
  };

  const getGoalForWeek = (weekStart) => {
    return goals.find(g => g.weekStartDate === weekStart);
  };

  const handlePrint = () => { window.print(); };

  const handleExportCSV = () => {
    const rows = [['Week Start', 'Week End', 'Status', 'Leads', 'Door Knocks', 'Phone Calls', 'Presentations', 'Sales', 'Premium', 'Future Appts', 'Referrals Received', 'Referrals Contacted']];
    activities.filter(a => a.status === 'submitted').forEach(a => {
      const t = { leadsWorked: 0, doorKnocks: 0, phoneCalls: 0, presentations: 0, sales: 0, annualizedPremium: 0, futureAppointments: 0, referralsReceived: 0, referralsContacted: 0 };
      (a.activities || []).forEach(d => { if (!d.offDay) Object.keys(t).forEach(k => { t[k] += (d[k] || 0); }); });
      rows.push([a.weekStartDate, a.weekEndDate, a.status, t.leadsWorked, t.doorKnocks, t.phoneCalls, t.presentations, t.sales, t.annualizedPremium.toFixed(2), t.futureAppointments, t.referralsReceived, t.referralsContacted]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'my_activity_reports.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const handleUnlock = async (weekStart) => {
    if (!window.confirm(`Unlock report for week of ${formatDateDisplay(weekStart)}?`)) return;
    const token = localStorage.getItem('agentToken');
    try {
      const result = await unlockActivity(weekStart, token);
      if (result.success) {
        toast.success('Report unlocked for editing');
        loadHistory(token);
      }
    } catch (error) {
      toast.error('Failed to unlock report');
    }
  };

  const calcTotals = (acts) => {
    const t = { leadsWorked: 0, doorKnocks: 0, phoneCalls: 0, presentations: 0, sales: 0, futureAppointments: 0, annualizedPremium: 0 };
    (acts || []).forEach(d => { if (!d.offDay) Object.keys(t).forEach(k => { t[k] += (d[k] || 0); }); });
    return t;
  };

  const isNow = (() => {
    const now = new Date();
    return currentMonth.year === now.getFullYear() && currentMonth.month === now.getMonth();
  })();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto"></div>
      </div>
    );
  }

  return (
    <AgentLayout title="Reports History">
      <div data-testid="reports-history-page">

      <div>
        {/* Calendar + Month Nav */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6 print:hidden">
          {/* Calendar */}
          <div className="lg:col-span-1">
            {selectedWeek && (
              <WeekCalendar
                weekStart={selectedWeek.start}
                weekEnd={selectedWeek.end}
                onWeekSelect={handleCalendarWeekSelect}
              />
            )}
          </div>

          {/* Month Nav + View Toggle */}
          <div className="lg:col-span-3 space-y-4">
            {/* Month Navigation */}
            <div className="bg-white rounded-lg shadow-md p-4" data-testid="month-navigation">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <button onClick={goToPrevMonth} className="flex items-center gap-2 px-4 py-2 min-h-[44px] bg-gray-100 hover:bg-gray-200 rounded-md transition text-sm" data-testid="prev-month-btn">
                  Previous Month
                </button>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl sm:text-2xl font-bold text-brand-blue" data-testid="current-month-label">{monthName}</h2>
                  {!isNow && (
                    <button onClick={goToCurrentMonth} className="px-3 py-1 min-h-[44px] bg-brand-orange text-white rounded-md text-sm font-semibold hover:bg-brand-orange-light transition" data-testid="current-month-btn">This Month</button>
                  )}
                </div>
                <button onClick={goToNextMonth} className="flex items-center gap-2 px-4 py-2 min-h-[44px] bg-gray-100 hover:bg-gray-200 rounded-md transition text-sm" data-testid="next-month-btn">
                  Next Month
                </button>
              </div>
            </div>

            {/* View Toggle */}
            <div className="bg-white rounded-lg shadow-md p-4" data-testid="report-type-toggle">
              <div className="flex flex-wrap gap-3 items-center">
                <button onClick={() => setViewType('activity')} className={`flex items-center gap-2 px-4 sm:px-6 py-2 min-h-[44px] rounded-md font-semibold transition ${viewType === 'activity' ? 'bg-brand-blue text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-testid="activity-reports-tab">
                  <FileText size={20} /> Activity Reports
                </button>
                <button onClick={() => setViewType('goals')} className={`flex items-center gap-2 px-4 sm:px-6 py-2 min-h-[44px] rounded-md font-semibold transition ${viewType === 'goals' ? 'bg-brand-orange text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-testid="goals-reports-tab">
                  <TrendingUp size={20} /> Goals Reports
                </button>
                <div className="flex-1" />
                <div className="flex gap-2">
                  <button onClick={handleExportCSV} className="flex items-center gap-1 px-3 py-2 min-h-[44px] bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition" data-testid="export-csv-btn">
                    <Download size={14} /> CSV
                  </button>
                  <button onClick={handlePrint} className="flex items-center gap-1 px-3 py-2 min-h-[44px] bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700 transition" data-testid="print-btn">
                    <Printer size={14} /> Print
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Reports - one card per week in the month */}
        {viewType === 'activity' && (
          <div className="space-y-4" data-testid="activity-reports-list">
            {weeksInMonth.map((week, i) => {
              const report = getReportForWeek(week.start);
              const totals = report ? calcTotals(report.activities) : null;
              const isSelected = selectedWeek?.start === week.start;

              return (
                <div
                  key={week.start}
                  className={`bg-white rounded-lg shadow-md p-5 border-l-4 transition ${
                    report ? (report.status === 'submitted' ? 'border-green-500' : 'border-yellow-400') : 'border-gray-200'
                  } ${isSelected ? 'ring-2 ring-brand-blue' : ''}`}
                  onClick={() => setSelectedWeek(week)}
                  data-testid={`activity-week-${i}`}
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div>
                        <span className="text-xs text-gray-400 font-semibold">WEEK {i + 1}</span>
                        <h3 className="text-base sm:text-lg font-bold text-brand-blue">
                          {formatDateDisplay(week.start)} - {formatDateDisplay(week.end)}
                        </h3>
                      </div>
                      {report ? (
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${report.status === 'submitted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {report.status === 'submitted' ? 'Submitted' : 'Draft'}
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">No Report</span>
                      )}
                    </div>
                    <div className="flex gap-2 print:hidden flex-wrap">
                      {report?.status === 'submitted' && (
                        <button onClick={(e) => { e.stopPropagation(); handleUnlock(week.start); }} className="flex items-center gap-1 bg-yellow-500 text-white px-3 py-1.5 rounded-md hover:bg-yellow-600 transition text-sm font-semibold" data-testid={`unlock-report-${i}`}>
                          <Unlock size={14} /> Unlock
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); navigate('/agent/dashboard'); }} className="bg-brand-blue text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition text-sm font-semibold" data-testid={`view-report-${i}`}>
                        {report ? 'View/Edit' : 'Start Report'}
                      </button>
                      {report && (
                        <button onClick={(e) => { e.stopPropagation(); handlePrint(); }} className="flex items-center gap-1 bg-gray-600 text-white px-3 py-1.5 rounded-md hover:bg-gray-700 transition text-sm font-semibold">
                          <Printer size={14} /> Print
                        </button>
                      )}
                      {report && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setSendModal({ open: true, type: viewType === 'activity' ? 'activity' : 'goals', week: week.start }); }}
                          className="flex items-center gap-1 bg-green-500 text-white px-3 py-1.5 rounded-md hover:bg-green-600 transition text-sm font-semibold"
                          data-testid={`send-report-email-${i}`}
                        >
                          <Mail size={14} /> Send
                        </button>
                      )}
                    </div>
                  </div>
                  {totals ? (
                    <div className="grid grid-cols-3 md:grid-cols-7 gap-3 border-t pt-3">
                      <div className="text-center"><p className="text-xl font-bold text-brand-blue">{totals.sales}</p><p className="text-xs text-gray-500">Sales</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-brand-blue">{totals.presentations}</p><p className="text-xs text-gray-500">Pres.</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-brand-blue">{totals.doorKnocks}</p><p className="text-xs text-gray-500">Knocks</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-brand-blue">{totals.phoneCalls}</p><p className="text-xs text-gray-500">Calls</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-brand-blue">${totals.annualizedPremium.toFixed(0)}</p><p className="text-xs text-gray-500">Premium</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-brand-blue">{totals.leadsWorked}</p><p className="text-xs text-gray-500">Leads</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-brand-blue">{totals.futureAppointments}</p><p className="text-xs text-gray-500">Appts</p></div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 border-t pt-3">No activity logged for this week</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Goals Reports */}
        {viewType === 'goals' && (
          <div className="space-y-4" data-testid="goals-reports-list">
            {weeksInMonth.map((week, i) => {
              const goal = getGoalForWeek(week.start);
              const isSelected = selectedWeek?.start === week.start;

              return (
                <div
                  key={week.start}
                  className={`bg-white rounded-lg shadow-md p-5 border-l-4 transition ${
                    goal ? 'border-brand-orange' : 'border-gray-200'
                  } ${isSelected ? 'ring-2 ring-brand-orange' : ''}`}
                  onClick={() => setSelectedWeek(week)}
                  data-testid={`goals-week-${i}`}
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div>
                        <span className="text-xs text-gray-400 font-semibold">WEEK {i + 1}</span>
                        <h3 className="text-base sm:text-lg font-bold text-brand-blue">
                          {formatDateDisplay(week.start)} - {formatDateDisplay(week.end)}
                        </h3>
                      </div>
                      {goal ? (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                          {goal.mode === 'weekly' ? 'Weekly Totals' : 'Daily Goals'}
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">No Goals Set</span>
                      )}
                    </div>
                    <div className="flex gap-2 print:hidden">
                      <button onClick={(e) => { e.stopPropagation(); navigate('/agent/goals'); }} className="bg-brand-orange text-white px-3 py-1.5 rounded-md hover:bg-brand-orange-light transition text-sm font-semibold">
                        {goal ? 'View/Edit' : 'Set Goals'}
                      </button>
                    </div>
                  </div>
                  {goal?.weeklyTotals ? (
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3 border-t pt-3">
                      <div className="text-center"><p className="text-xl font-bold text-brand-orange">{goal.weeklyTotals.salesGoal || 0}</p><p className="text-xs text-gray-500">Sales Goal</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-brand-orange">{goal.weeklyTotals.presentationsGoal || 0}</p><p className="text-xs text-gray-500">Pres.</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-brand-orange">{goal.weeklyTotals.doorKnocksGoal || 0}</p><p className="text-xs text-gray-500">Knocks</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-brand-orange">{goal.weeklyTotals.phoneCallsGoal || 0}</p><p className="text-xs text-gray-500">Calls</p></div>
                      <div className="text-center"><p className="text-xl font-bold text-brand-orange">${goal.weeklyTotals.annualizedPremiumGoal?.toFixed(0) || 0}</p><p className="text-xs text-gray-500">Premium</p></div>
                    </div>
                  ) : !goal ? (
                    <p className="text-sm text-gray-400 border-t pt-3">No goals set for this week</p>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>

      <SendReportModal
        isOpen={sendModal.open}
        onClose={() => setSendModal({ open: false, type: '', week: '' })}
        reportType={sendModal.type}
        weekStartDate={sendModal.week}
      />
    </AgentLayout>
  );
};

export default ReportsHistory;
